import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  DisconnectOutlined,
  EditOutlined,
  FileExcelOutlined,
  LinkOutlined,
  NodeIndexOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Loading from "components/shared-components/Loading";
import Flex from "components/shared-components/Flex";
import ExcelExport from "components/shared-components/ExcelExport";
import { WAREHOUSE_FIELDS } from "constants/ProductSyncConstant";
import {
  buildProductRows,
  compareRoutePosition,
  filterProductRows,
  formatGrams,
  narrowSyncPlan,
  needsPackCheck,
  notSelectedProperties,
  SHOP_FILTER_ALL,
  summariseProductRows,
  warehouseValuesOf,
} from "functions/productData";
import { PACK_ADVICE, piecesPerPackFor } from "functions/packaging";
import { buildSyncPlan, linkCounterpart } from "functions/productSync";
import { articlesForProduct, buildArticleCatalogue, isPackageProduct, productKey, weightLabel } from "functions/articleCode";
import { buildLinkProposal, linkEntriesForProposals } from "functions/productLinkPlan";
import { usePickRoute, useProductFields, useProductLinks, useProducts } from "data/hooks";
import LinkProductModal from "./LinkProductModal";
import LinkProposalModal from "./LinkProposalModal";
import ProductWarehouseDrawer from "./ProductWarehouseDrawer";
import StaleWriteModal from "./StaleWriteModal";
import SyncPromptModal from "./SyncPromptModal";
import SyncedPropertiesModal from "./SyncedPropertiesModal";
import { SHOP_COLORS, shopLabel } from "./constants";
import { useHvoSession } from "./useHvoSession";

const { Text } = Typography;

/** The fields the drawer reads on open: the editable ones plus who last touched them. */
const DRAWER_FIELDS = [...WAREHOUSE_FIELDS, "updatedAt", "updatedBy"];

/**
 * Orderpicking -> Productgegevens.
 *
 * Vleescentrum and Halalvlees Online are two storefronts on nearly the same products, picked
 * from one freezer, and until now their product data was maintained in two separate CMSes.
 * This screen holds the part the warehouse cares about - pick weight, volume weight, pieces per
 * package, note for the picker - for both shops in one table, and when a value changes on a
 * product that is linked to its counterpart in the other shop it says so and offers to copy it.
 *
 * Deliberately only those fields: the rest of product management still lives in `cms/`,
 * which writes the whole product document. Everything here is a targeted field update, and
 * every save is checked against what was on screen when the drawer opened.
 */
const ProductData = () => {
  const { t } = useTranslation();

  const { products, isLoading: productsLoading, error: productsError, refreshProducts } = useProducts();
  const { entries: pickRoute, isLoading: routeLoading } = usePickRoute();
  const {
    links,
    isLoading: linksLoading,
    isLoaded: linksLoaded,
    error: linksError,
    refresh: refreshLinks,
    getLinkForProduct,
    createLink,
    createLinks,
    removeLink,
    updateSyncedProperties,
  } = useProductLinks();
  const { readFields, updateWarehouseFields, applySyncPlan } = useProductFields();
  const hvoSession = useHvoSession();

  const [searchText, setSearchText] = useState("");
  const [shopFilter, setShopFilter] = useState(SHOP_FILTER_ALL);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [onlyPackCheck, setOnlyPackCheck] = useState(false);

  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState(null);
  const [editMeta, setEditMeta] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [stale, setStale] = useState(null);
  const [sync, setSync] = useState(null);
  const [applying, setApplying] = useState(false);

  const [linkRow, setLinkRow] = useState(null);
  const [linking, setLinking] = useState(false);

  const [proposalOpen, setProposalOpen] = useState(false);
  const [linkProposal, setLinkProposal] = useState(null);
  const [proposalComputing, setProposalComputing] = useState(false);
  const [creatingLinks, setCreatingLinks] = useState(false);
  const [linkProgress, setLinkProgress] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const [propertiesLink, setPropertiesLink] = useState(null);
  const [savingProperties, setSavingProperties] = useState(false);

  const isLoading = productsLoading || linksLoading || routeLoading;

  /**
   * Linking needs the existing links, not just the products.
   *
   * A link is one-to-one, and the check that keeps it that way reads this same list. If the
   * read failed - which on the very first run means the Firestore rules for the new
   * `productLinks` collection are not deployed yet - every product reads as unlinked, and
   * linking from that state would quietly create a second link for a pair that already has one.
   * Not being able to link for a minute is the cheaper failure, so the button waits.
   */
  const canLink = linksLoaded;

  const rows = useMemo(() => buildProductRows({ products, links, pickRoute }), [products, links, pickRoute]);
  // Article codes (HVC-44) are derived, never stored: one per weight the product is sold or
  // packed in, the base code for a product without weights. What the vakken and the pick
  // rounds are keyed on, shown here so the warehouse can check and export them.
  const articleCatalogue = useMemo(() => buildArticleCatalogue(products), [products]);
  const articleExport = useMemo(() => {
    const productsByKey = new Map(products.map((product) => [productKey(product.shopSource, product.id), product]));
    const data = [];
    articleCatalogue.forEach((entry) => {
      const product = productsByKey.get(entry.key) || {};
      entry.articles.forEach((article) => {
        data.push([
          { value: entry.shopSource },
          { value: entry.name },
          { value: article.articleCode },
          { value: article.weight === null ? "" : article.weight },
          { value: article.sources.join(", ") },
          { value: product.pickWeightGram === undefined || product.pickWeightGram === null ? "" : product.pickWeightGram },
          { value: product.volumeWeightGram === undefined || product.volumeWeightGram === null ? "" : product.volumeWeightGram },
          { value: piecesPerPackFor(product, article.weight) || "" },
        ]);
      });
    });
    return [
      {
        sheetName: "Artikelcodes",
        columns: [
          { title: "Shop" },
          { title: "Product" },
          { title: "Artikelcode" },
          { title: "Gewicht (g)" },
          { title: "Bron" },
          { title: "Pickgewicht (g)" },
          { title: "Volumegewicht (g)" },
          { title: "Stuks per verpakking" },
        ],
        data,
      },
    ];
  }, [articleCatalogue, products]);
  const rowByKey = useMemo(() => new Map(rows.map((row) => [row.key, row])), [rows]);
  const summary = useMemo(() => summariseProductRows(rows), [rows]);

  const filteredRows = useMemo(
    () => filterProductRows(rows, { search: searchText, shop: shopFilter, onlyMissing, onlyUnlinked, onlyPackCheck }),
    [rows, searchText, shopFilter, onlyMissing, onlyUnlinked, onlyPackCheck],
  );
  const packCheckCount = useMemo(() => rows.filter(needsPackCheck).length, [rows]);

  /**
   * The bulk proposal, computed only while its modal is open: it walks both catalogues against
   * each other and there is no reason to pay for that on every keystroke in the search box.
   *
   * In an effect rather than a `useMemo`, and that is the whole point. A memo runs *during*
   * render: on two catalogues of a thousand products the modal would only appear once the walk
   * had finished, so the click does nothing for the better part of a second and the screen
   * looks stuck. Here the modal opens first, says it is working, and the walk runs right after
   * that paint.
   *
   * It is still synchronous once it starts - this is one function over two arrays, not
   * something to hand to a worker - so the timeout is what buys the paint, not the work.
   *
   * A recomputation (both hooks revalidate on reconnect) deliberately leaves the previous
   * proposal on screen while it runs; someone may be halfway through ticking it. The modal
   * shows a small note next to it instead of blanking the table.
   */
  useEffect(() => {
    if (!proposalOpen) {
      setLinkProposal(null);
      setProposalComputing(false);
      return undefined;
    }

    let cancelled = false;
    setProposalComputing(true);

    const timer = setTimeout(() => {
      if (cancelled) return;
      const next = buildLinkProposal({ products, links, pickRoute });
      if (cancelled) return;
      setLinkProposal(next);
      setProposalComputing(false);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [proposalOpen, products, links, pickRoute]);

  /** Whether this session may write to that shop at all - HVO needs its own session. */
  const canWriteToShopSource = (shopSource) => shopSource !== "HVO" || hvoSession;

  const canEditRow = (row) => !!row && canWriteToShopSource(row.shopSource);

  const counterpartRowOf = (row) =>
    !row || !row.counterpart
      ? null
      : rowByKey.get(`${row.counterpart.shopSource}|${row.counterpart.productId}`) || null;

  // --- editing ---------------------------------------------------------------------------

  /**
   * Load the product's stored values straight from Firestore rather than from the cached list:
   * the cache can be minutes old, and it is this snapshot the save is checked against.
   */
  const loadEditor = async (row) => {
    setEditLoading(true);
    try {
      const fresh = await readFields(row.shopSource, row.id, DRAWER_FIELDS);
      if (!fresh) {
        message.error(t("product_data.product_gone"), 6);
        closeEditor();
        return;
      }
      setEditValues(warehouseValuesOf(fresh));
      setEditMeta({ updatedAt: fresh.updatedAt || null, updatedBy: fresh.updatedBy || null });
    } catch (error) {
      // Reading the other project can fail on its own (no session, rules). Fall back to the
      // cached values so the drawer still shows something, and say that it is not fresh.
      setEditValues(warehouseValuesOf(row.product));
      setEditMeta({ updatedAt: row.updatedAt, updatedBy: row.updatedBy });
      message.warning(t("product_data.read_failed"), 6);
    } finally {
      setEditLoading(false);
    }
  };

  const openEditor = async (row) => {
    setEditRow(row);
    setEditValues(null);
    setEditMeta(null);
    setStale(null);
    await loadEditor(row);
  };

  const closeEditor = () => {
    setEditRow(null);
    setEditValues(null);
    setEditMeta(null);
    setStale(null);
  };

  /**
   * After a save that really changed something: is this product linked, and would copying the
   * change to the other shop do anything? Only then is there a question worth asking.
   *
   * The other product is re-read first, so the melding states its current values rather than
   * whatever the cached list still held.
   */
  const offerSync = async (row, written, changedKeys, options = {}) => {
    const link = getLinkForProduct(row.shopSource, row.id);
    if (!link) return;

    const counterpart = linkCounterpart(link, row.shopSource);
    if (!counterpart || !counterpart.productId) return;

    let targetValues = null;
    try {
      targetValues = await readFields(counterpart.shopSource, counterpart.productId);
    } catch (error) {
      message.warning(t("product_data.sync_target_unreadable", { shop: shopLabel(counterpart.shopSource) }), 6);
      return;
    }
    if (!targetValues) {
      message.warning(t("product_data.sync_target_missing"), 6);
      return;
    }

    const plan = buildSyncPlan({
      source: written,
      target: targetValues,
      changedProperties: changedKeys,
      syncedProperties: link.syncedProperties,
    });

    const counterpartRow = rowByKey.get(`${counterpart.shopSource}|${counterpart.productId}`);
    const target = {
      shopSource: counterpart.shopSource,
      productId: counterpart.productId,
      name: (counterpartRow && counterpartRow.name) || counterpart.name || "",
    };

    if (!plan.hasChanges) {
      const skipped = notSelectedProperties(plan);
      if (skipped.length > 0) {
        message.info(
          t("product_data.sync_only_not_selected", {
            properties: skipped.map((entry) => entry.label).join(", "),
            shop: shopLabel(target.shopSource),
          }),
          6,
        );
      }
      return;
    }

    setSync({
      plan,
      selectedKeys: plan.changes.map((change) => change.key),
      source: { name: row.name, shopSource: row.shopSource },
      target,
      targetValues,
      link,
      row,
      written,
      changedKeys,
      retried: !!options.retried,
    });
  };

  const handleSave = async (formValues, options = {}) => {
    if (!editRow) return;
    const row = editRow;

    setSaving(true);
    try {
      const result = await updateWarehouseFields({
        shopSource: row.shopSource,
        productId: row.id,
        values: formValues,
        // A deliberate overwrite skips the check: the user has just been shown what they are
        // overwriting and said to do it anyway.
        loadedValues: options.force ? null : editValues,
      });

      if (!result.ok) {
        if (result.reason === "stale") {
          setStale({ fields: result.staleFields, values: formValues });
          return;
        }
        message.error(result.message, 6);
        return;
      }

      closeEditor();

      if (result.changed.length === 0) {
        message.info(t("product_data.nothing_changed"), 3);
        return;
      }

      message.success(t("product_data.saved", { product: row.name }), 3);
      await offerSync(row, result.written, result.changed.map((change) => change.key));
    } catch (error) {
      message.error(error.message, 6);
    } finally {
      setSaving(false);
    }
  };

  const handleStaleReload = async () => {
    const row = editRow;
    setStale(null);
    if (!!row) await loadEditor(row);
  };

  const handleStaleOverwrite = async () => {
    if (!stale) return;
    const values = stale.values;
    setStale(null);
    await handleSave(values, { force: true });
  };

  // --- the sync prompt --------------------------------------------------------------------

  const handleApplySync = async () => {
    if (!sync) return;
    // The prompt is shown even without a session for the target shop - seeing that a
    // counterpart exists is useful - but the button that writes to it is not live then.
    if (!canWriteToShopSource(sync.target.shopSource)) return;

    const narrowed = narrowSyncPlan(sync.plan, sync.selectedKeys);
    if (!narrowed || !narrowed.hasChanges) {
      setSync(null);
      return;
    }

    setApplying(true);
    try {
      const result = await applySyncPlan({
        shopSource: sync.target.shopSource,
        productId: sync.target.productId,
        plan: narrowed,
      });

      if (result.ok) {
        message.success(
          t("product_data.sync_applied", {
            product: sync.target.name,
            shop: shopLabel(sync.target.shopSource),
          }),
          4,
        );
        setSync(null);
        return;
      }

      // The other product moved between building the plan and applying it. Nothing was
      // written; ask again with the values it holds now rather than forcing the old ones.
      if (result.reason === "stale" && !sync.retried) {
        const context = sync;
        setSync(null);
        message.warning(t("product_data.sync_target_changed"), 6);
        await offerSync(context.row, context.written, context.changedKeys, { retried: true });
        return;
      }

      message.error(result.message, 6);
      setSync(null);
    } catch (error) {
      message.error(error.message, 6);
    } finally {
      setApplying(false);
    }
  };

  // --- links ------------------------------------------------------------------------------

  const handleCreateLink = async (candidate) => {
    if (!linkRow || !candidate) return;
    if (!canLink) {
      message.error(t("product_data.links_unavailable"), 6);
      return;
    }

    setLinking(true);
    try {
      const vceProduct = linkRow.shopSource === "VCE" ? linkRow.product : candidate;
      const hvoProduct = linkRow.shopSource === "HVO" ? linkRow.product : candidate;
      const result = await createLink({ vceProduct, hvoProduct });

      if (!result.ok) {
        message.error(
          result.reason === "links-unavailable"
            ? t("product_data.links_unavailable")
            : t("product_data.link_taken", { shop: shopLabel(result.shopSource) }),
          6,
        );
        return;
      }

      message.success(
        t("product_data.link_created", { vce: vceProduct.name || "-", hvo: hvoProduct.name || "-" }),
        4,
      );
      setLinkRow(null);
    } catch (error) {
      message.error(
        error.message === "productLinks/incomplete" ? t("product_data.link_incomplete") : error.message,
        6,
      );
    } finally {
      setLinking(false);
    }
  };

  const closeProposal = () => {
    setProposalOpen(false);
    setBulkResult(null);
    setLinkProgress(null);
  };

  /**
   * Create everything that was ticked in the proposal, in one round.
   *
   * The plan already refuses to tick a conflict, and the hook checks one-to-one again against
   * both the stored links and the rest of the batch - twice on purpose, because what arrives
   * here can also come from a screen that has been open a while.
   */
  const handleCreateLinks = async (selectedKeys) => {
    if (!linkProposal) return;
    if (!canLink) {
      message.error(t("product_data.links_unavailable"), 6);
      return;
    }

    const chosen = new Set(selectedKeys || []);
    const entries = linkEntriesForProposals(
      linkProposal.proposals.filter((proposal) => chosen.has(proposal.key)),
    );
    if (entries.length === 0) return;

    setCreatingLinks(true);
    setLinkProgress({ committed: 0, total: entries.length });
    try {
      const result = await createLinks(entries, { onProgress: setLinkProgress });

      // Nothing was attempted, so there is no per-entry result worth showing.
      if (!result.ok && result.reason === "links-unavailable") {
        message.error(t("product_data.links_unavailable"), 6);
        return;
      }

      // A partial write still goes on screen: what did land is real, and the rest has to be
      // visible per product rather than disappearing behind one red bar.
      setBulkResult(result);

      if (!result.ok) {
        message.error(
          t("product_data.proposal_write_failed", {
            error: (result.error && result.error.message) || "",
          }),
          8,
        );
        return;
      }

      if (result.created > 0) {
        message.success(t("product_data.proposal_created", { amount: result.created }), 4);
      }
    } catch (error) {
      message.error(error.message, 6);
    } finally {
      setCreatingLinks(false);
    }
  };

  const handleRemoveLink = async (row) => {
    try {
      await removeLink(row.link.id);
      message.success(t("product_data.link_removed"), 3);
    } catch (error) {
      message.error(error.message, 6);
    }
  };

  const handleSaveProperties = async (keys) => {
    if (!propertiesLink) return;

    setSavingProperties(true);
    try {
      const result = await updateSyncedProperties(propertiesLink.id, keys);
      setPropertiesLink(null);
      message.success(t("product_data.synced_properties_saved"), 3);

      // Changing this while the melding is open changes the melding: rebuild it against the
      // values already read, so the prompt on screen is never about the old setting.
      if (!!sync && sync.link.id === propertiesLink.id) {
        const plan = buildSyncPlan({
          source: sync.written,
          target: sync.targetValues,
          changedProperties: sync.changedKeys,
          syncedProperties: result.syncedProperties,
        });

        if (!plan.hasChanges) {
          setSync(null);
          message.info(t("product_data.sync_nothing_left"), 5);
          return;
        }

        setSync({
          ...sync,
          plan,
          selectedKeys: plan.changes.map((change) => change.key),
          link: { ...sync.link, syncedProperties: result.syncedProperties },
        });
      }
    } catch (error) {
      message.error(error.message, 6);
    } finally {
      setSavingProperties(false);
    }
  };

  // --- table ------------------------------------------------------------------------------

  const gramsCell = (value) =>
    value === null ? <Text type="secondary">{formatGrams(null)}</Text> : formatGrams(value);

  const columns = [
    {
      title: t("product_data.shop"),
      dataIndex: "shopSource",
      width: 90,
      render: (shopSource) => <Tag color={SHOP_COLORS[shopSource] || "default"}>{shopSource}</Tag>,
    },
    // Every column has a width: with the ellipsis on the pick note the table is laid out fixed, and
    // a column without one is squeezed to nothing on a laptop screen instead of the table scrolling.
    {
      title: t("product"),
      dataIndex: "name",
      width: 220,
      sorter: (a, b) => a.name.localeCompare(b.name, "nl"),
      render: (name, row) => <strong>{name || row.id}</strong>,
    },
    {
      title: (
        <Tooltip title={t("product_data.vak_help")}>
          <span>{t("product_data.vak")}</span>
        </Tooltip>
      ),
      dataIndex: "vakken",
      width: 190,
      sorter: compareRoutePosition,
      render: (vakken) => {
        if (vakken.length === 0) return <Text type="secondary">-</Text>;
        const shown = vakken.slice(0, 3);
        return (
          <Space direction="vertical" size={0}>
            {shown.map((vak, index) => (
              <span key={`${vak.sequence}-${index}`} style={{ whiteSpace: "nowrap" }}>
                {vak.location ? (
                  <strong style={{ fontFamily: "monospace" }}>{vak.location}</strong>
                ) : (
                  <Text type="secondary">{t("product_data.vak_no_code")}</Text>
                )}
                {vak.weight !== null && <Text type="secondary"> · {weightLabel(vak.weight)}</Text>}
              </span>
            ))}
            {vakken.length > shown.length && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("product_data.article_more", { count: vakken.length - shown.length })}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t("product_data.article_codes"),
      dataIndex: "articleCodes",
      width: 230,
      render: (_, row) => {
        const product = products.find((candidate) => candidate.id === row.id && candidate.shopSource === row.shopSource);
        if (isPackageProduct(product)) return <Tag>{t("product_data.article_package")}</Tag>;
        const articles = (articleCatalogue.get(productKey(row.shopSource, row.id)) || {}).articles || [];
        if (articles.length === 0) return <Text type="secondary">-</Text>;
        const shown = articles.slice(0, 3);
        return (
          <Tooltip title={articles.map((article) => article.articleCode).join("\n")}>
            <Space direction="vertical" size={0}>
              {shown.map((article) => (
                <Text key={article.articleCode} code copyable={{ text: article.articleCode }} style={{ fontSize: 12 }}>
                  {article.articleCode}
                </Text>
              ))}
              {articles.length > shown.length && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("product_data.article_more", { count: articles.length - shown.length })}
                </Text>
              )}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: t("product_data.pick_weight_short"),
      dataIndex: "pickWeightGram",
      width: 130,
      sorter: (a, b) => (a.pickWeightGram || 0) - (b.pickWeightGram || 0),
      render: gramsCell,
    },
    {
      title: t("product_data.volume_weight_short"),
      dataIndex: "volumeWeightGram",
      width: 140,
      sorter: (a, b) => (a.volumeWeightGram || 0) - (b.volumeWeightGram || 0),
      render: gramsCell,
    },
    {
      title: (
        <Tooltip title={t("product_data.pieces_per_pack_column_help")}>
          <span>{t("product_data.pieces_per_pack_short")}</span>
        </Tooltip>
      ),
      dataIndex: "piecesPerPackMax",
      width: 240,
      sorter: (a, b) => a.piecesPerPackMax - b.piecesPerPackMax,
      render: (_, row) => {
        // A package has no articles of its own: its components are picked
        if (!row.packs || row.packs.length === 0) return <Text type="secondary">-</Text>;
        const adviceTag = (pack) => {
          const units = (pack.advice.orderUnits || []).join(" / ");
          if (pack.advice.kind === PACK_ADVICE.SUGGEST) {
            return (
              <Tooltip title={t("product_data.pieces_per_pack_suggest", { units })}>
                <Tag style={{ marginLeft: 4 }}>{t("product_data.pieces_per_pack_webshop", { units })}</Tag>
              </Tooltip>
            );
          }
          if (pack.advice.kind === PACK_ADVICE.LOOSE) {
            return (
              <Tooltip title={t("product_data.pieces_per_pack_loose", { units, size: pack.advice.size })}>
                <Tag color="orange" style={{ marginLeft: 4 }}>
                  {t("product_data.pieces_per_pack_webshop", { units })}
                </Tag>
              </Tooltip>
            );
          }
          return null;
        };
        const valueOf = (pack) =>
          pack.piecesPerPack ? (
            t("product_data.pieces_per_pack_value", { count: pack.piecesPerPack })
          ) : (
            <Text type="secondary">{t("product_data.pieces_per_pack_none")}</Text>
          );
        // Without weights: one article, one value
        if (row.packs.every((pack) => pack.weight === null)) {
          return (
            <span>
              {valueOf(row.packs[0])}
              {adviceTag(row.packs[0])}
            </span>
          );
        }
        // Per weight: the ones packed per several or worth a look; the rest is per piece
        const notable = row.packs.filter((pack) => !!pack.piecesPerPack || pack.advice.kind !== PACK_ADVICE.NONE);
        if (notable.length === 0) return <Text type="secondary">{t("product_data.pieces_per_pack_none")}</Text>;
        const shown = notable.slice(0, 3);
        return (
          <Space direction="vertical" size={0}>
            {shown.map((pack) => (
              <span key={pack.articleCode} style={{ whiteSpace: "nowrap" }}>
                <Text type="secondary">{weightLabel(pack.weight)} · </Text>
                {valueOf(pack)}
                {adviceTag(pack)}
              </span>
            ))}
            {notable.length > shown.length && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("product_data.article_more", { count: notable.length - shown.length })}
              </Text>
            )}
            {notable.length < row.packs.length && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("product_data.pieces_per_pack_rest")}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t("product_data.pick_note"),
      dataIndex: "pickNote",
      width: 180,
      ellipsis: true,
      render: (pickNote) =>
        !pickNote ? <Text type="secondary">-</Text> : <Tooltip title={pickNote}>{pickNote}</Tooltip>,
    },
    {
      title: t("product_data.link_column"),
      dataIndex: "isLinked",
      width: 240,
      render: (_, row) => {
        // Without the links, "Niet gekoppeld" would be a claim this screen cannot make.
        if (!canLink) return <Text type="secondary">-</Text>;
        if (!row.isLinked) return <Tag>{t("product_data.not_linked")}</Tag>;
        const counterpartRow = counterpartRowOf(row);
        const name = (counterpartRow && counterpartRow.name) || row.counterpart.name || row.counterpart.productId;
        return (
          <Tooltip title={t("product_data.linked_tooltip", { shop: shopLabel(row.counterpart.shopSource) })}>
            <Tag color={SHOP_COLORS[row.counterpart.shopSource] || "default"} icon={<LinkOutlined />}>
              {name}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      width: 140,
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title={canEditRow(row) ? t("edit") : t("product_data.no_hvo_session_tooltip")}>
            {/* span so the tooltip still works while the button is disabled */}
            <span>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={!canEditRow(row)}
                onClick={() => openEditor(row)}
              />
            </span>
          </Tooltip>

          {!row.isLinked ? (
            <Tooltip title={canLink ? t("product_data.link_action") : t("product_data.links_unavailable")}>
              {/* span so the tooltip still works while the button is disabled */}
              <span>
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  disabled={!canLink}
                  onClick={() => setLinkRow(row)}
                />
              </span>
            </Tooltip>
          ) : (
            <>
              <Tooltip title={t("product_data.synced_properties_button")}>
                <Button size="small" icon={<SettingOutlined />} onClick={() => setPropertiesLink(row.link)} />
              </Tooltip>
              <Popconfirm
                title={t("product_data.unlink_confirm")}
                description={t("product_data.unlink_description")}
                okText={t("yes")}
                cancelText={t("no")}
                onConfirm={() => handleRemoveLink(row)}
              >
                <Tooltip title={t("product_data.unlink")}>
                  <Button size="small" danger icon={<DisconnectOutlined />} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  if (isLoading) return <Loading cover="content" />;

  const vceSummary = summary.shops.VCE || { total: 0, linked: 0, missing: 0 };
  const hvoSummary = summary.shops.HVO || { total: 0, linked: 0, missing: 0 };

  return (
    <Card
      title={t("product_data.title")}
      extra={
        <Space>
          <Tooltip title={canLink ? t("product_data.proposal_button_help") : t("product_data.links_unavailable")}>
            {/* span so the tooltip still works while the button is disabled */}
            <span>
              <Button
                type="primary"
                icon={<NodeIndexOutlined />}
                disabled={!canLink}
                onClick={() => {
                  setBulkResult(null);
                  setLinkProgress(null);
                  setProposalOpen(true);
                }}
              >
                {t("product_data.proposal_button")}
              </Button>
            </span>
          </Tooltip>
          <ExcelExport data={articleExport} fileName="artikelcodes">
            <Button icon={<FileExcelOutlined />} disabled={articleCatalogue.size === 0}>
              {t("product_data.export_article_codes")}
            </Button>
          </ExcelExport>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              refreshProducts();
              refreshLinks();
            }}
          >
            {t("product_data.refresh")}
          </Button>
        </Space>
      }
    >
      <p style={{ maxWidth: 820, color: "#6b6b6b" }}>{t("product_data.explanation")}</p>

      {!!productsError && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={t("product_data.products_error")}
          description={productsError.message}
        />
      )}

      {(!!linksError || !canLink) && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={t("product_data.links_error")}
          description={
            <>
              <div>
                {canLink
                  ? // The links are on screen, they just could not be refreshed: nothing has to
                    // be blocked, but the list may be a few minutes behind.
                    t("product_data.links_error_stale")
                  : t("product_data.links_error_description")}
              </div>
              {!!linksError && !!linksError.message && (
                <Text type="secondary">{linksError.message}</Text>
              )}
            </>
          }
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refreshLinks()}>
              {t("product_data.refresh")}
            </Button>
          }
        />
      )}

      {!hvoSession && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message={t("product_data.no_hvo_session_title")}
          description={t("product_data.no_hvo_session_screen")}
        />
      )}

      <Row gutter={16} className="mb-4">
        <Col xs={12} md={6}>
          <Statistic
            title={t("product_data.stat_vce")}
            value={vceSummary.total}
            suffix={
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("product_data.stat_missing_suffix", { count: vceSummary.missing })}
              </Text>
            }
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title={t("product_data.stat_hvo")}
            value={hvoSummary.total}
            suffix={
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("product_data.stat_missing_suffix", { count: hvoSummary.missing })}
              </Text>
            }
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title={t("product_data.stat_linked")}
            value={summary.linked}
            suffix={
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("product_data.stat_of_total", { count: summary.total })}
              </Text>
            }
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title={t("product_data.stat_missing")}
            value={summary.missing}
            valueStyle={summary.missing > 0 ? { color: "#ff7300" } : undefined}
            suffix={
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("product_data.stat_of_total", { count: summary.total })}
              </Text>
            }
          />
        </Col>
      </Row>

      <Flex justifyContent="between" alignItems="center" className="mb-3 flex-wrap">
        <Space wrap>
          <Input
            value={searchText}
            placeholder={t("search")}
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 280 }}
            onChange={(event) => setSearchText(event.currentTarget.value)}
          />
          <Select
            value={shopFilter}
            style={{ width: 200 }}
            onChange={setShopFilter}
            options={[
              { value: SHOP_FILTER_ALL, label: t("product_data.all_shops") },
              { value: "VCE", label: shopLabel("VCE") },
              { value: "HVO", label: shopLabel("HVO") },
            ]}
          />
          <Tooltip title={t("product_data.only_missing_help")}>
            <Checkbox checked={onlyMissing} onChange={(event) => setOnlyMissing(event.target.checked)}>
              {t("product_data.only_missing")}
            </Checkbox>
          </Tooltip>
          {/* Without the links every row is "unlinked", so the filter would be a lie too. */}
          <Tooltip title={canLink ? "" : t("product_data.links_unavailable")}>
            <Checkbox
              checked={onlyUnlinked}
              disabled={!canLink}
              onChange={(event) => setOnlyUnlinked(event.target.checked)}
            >
              {t("product_data.only_unlinked")}
            </Checkbox>
          </Tooltip>
          <Tooltip title={t("product_data.only_pack_check_help")}>
            <Checkbox checked={onlyPackCheck} onChange={(event) => setOnlyPackCheck(event.target.checked)}>
              {t("product_data.only_pack_check", { count: packCheckCount })}
            </Checkbox>
          </Tooltip>
        </Space>
        <span style={{ color: "#6b6b6b" }}>
          {t("product_data.row_count", { count: filteredRows.length, total: summary.total })}
        </span>
      </Flex>

      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={columns}
          dataSource={filteredRows}
          rowKey="key"
          size="small"
          pagination={{ defaultPageSize: 50, showSizeChanger: true }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("product_data.no_rows")} />
            ),
          }}
        />
      </div>

      <ProductWarehouseDrawer
        open={!!editRow}
        row={editRow}
        articles={editRow ? articlesForProduct(articleCatalogue, editRow.shopSource, editRow.id) : []}
        values={editValues}
        meta={editMeta}
        loading={editLoading}
        saving={saving}
        canEdit={canEditRow(editRow)}
        counterpartName={
          !editRow || !editRow.counterpart
            ? null
            : (counterpartRowOf(editRow) || {}).name || editRow.counterpart.name || editRow.counterpart.productId
        }
        onSave={handleSave}
        onEditSyncedProperties={() => setPropertiesLink(editRow && editRow.link)}
        onClose={closeEditor}
      />

      <StaleWriteModal
        open={!!stale}
        staleFields={stale && stale.fields}
        busy={saving}
        onReload={handleStaleReload}
        onOverwrite={handleStaleOverwrite}
        onClose={() => setStale(null)}
      />

      <SyncPromptModal
        open={!!sync}
        plan={sync && sync.plan}
        selectedKeys={(sync && sync.selectedKeys) || []}
        source={sync && sync.source}
        target={sync && sync.target}
        applying={applying}
        canApply={!sync || canWriteToShopSource(sync.target.shopSource)}
        onChange={(keys) => setSync({ ...sync, selectedKeys: keys })}
        onApply={handleApplySync}
        onSkip={() => setSync(null)}
        onEditSyncedProperties={() => setPropertiesLink(sync && sync.link)}
      />

      <LinkProductModal
        open={!!linkRow}
        row={linkRow}
        products={products}
        links={links}
        pickRoute={pickRoute}
        linking={linking}
        onConfirm={handleCreateLink}
        onClose={() => setLinkRow(null)}
      />

      <LinkProposalModal
        open={proposalOpen}
        proposal={linkProposal}
        loading={proposalComputing}
        creating={creatingLinks}
        progress={linkProgress}
        result={bulkResult}
        canLink={canLink}
        onCreate={handleCreateLinks}
        onClose={closeProposal}
      />

      <SyncedPropertiesModal
        open={!!propertiesLink}
        link={propertiesLink}
        saving={savingProperties}
        onSave={handleSaveProperties}
        onClose={() => setPropertiesLink(null)}
      />
    </Card>
  );
};

export default ProductData;
